import { color } from '../types/constants';
import {
  Bets,
  ClientBetsType,
  IBets,
  IClientBet,
  IMakeBet,
  ISelection,
  ModifiedBets,
} from '../types';
import {
  BetsTypes,
  numberOnTheRacetrack,
  numberOnTheRacetrackValueIndex,
  chancesBets,
  columnBets,
  dozenBets,
  raceTrackBets,
  neighboursChipsCount,
} from '../const';
import { pathOr } from 'ramda';
import { getBetColorType, isFirst, isOdd, isRed } from './betHelpers';

import { calculateIndexes } from '../BetsArea/common';

const {
  greenHaze,
  fireRed,
  deepBlack,
  newRedLight,
  newRedDark,
  newGreenLight,
  newGreenDark,
  newBlackLight,
  newBlackDark,
  smokyBlack,
} = color;

export const getCellColor = (value: string, black = deepBlack, red = fireRed) => {
  switch (getBetColorType(value)) {
    case BetsTypes.zero:
      return {
        color: greenHaze,
        colorStart: newGreenLight,
        colorEnd: newGreenDark,
        gradientId: newGreenDark,
      };
    case BetsTypes.black:
      return {
        color: black,
        colorStart: newBlackLight,
        colorEnd: newBlackDark,
        gradientId: newBlackDark,
      };
    case BetsTypes.red:
      return {
        color: red,
        colorStart: newRedLight,
        colorEnd: newRedDark,
        gradientId: newRedDark,
      };
  }
};

export const getRedBlackColor = (index: number) => {
  if (index === 2) {
    return {
      color: fireRed,
      colorStart: color.newRedLight,
      colorEnd: color.newRedDark,
      gradientId: color.newRedDark,
    };
  }

  if (index === 3) {
    return {
      color: deepBlack,
      colorStart: color.newBlackLight,
      colorEnd: color.newBlackDark,
      gradientId: color.newBlackDark,
    };
  }

  return {
    color: smokyBlack,
    colorStart: color.newGrayLight,
    colorEnd: color.newGrayDark,
    gradientId: color.newGrayDark,
  };
};

export const calculateColorForDozen = (index: number) => {
  return {
    color: smokyBlack,
    colorStart: color.newGrayLight,
    colorEnd: color.newGrayDark,
    gradientId: color.newGrayDark,
  };
};

export function getBetPath<T extends IClientBet>(bet: T) {
  const { type, selector } = bet;
  const currentKey = selector.toString();

  return { type, currentKey };
}

export const sumBets = (bets: IClientBet[]) =>
  bets.reduce((sum: number, { bet = 0, type }: IClientBet) => {
    if (
      raceTrackBets.has(type) ||
      type === BetsTypes.hotColdStraight ||
      type === BetsTypes.historyStraight
    ) {
      return sum;
    }

    return sum + bet;
  }, 0);

export const betsToBetsArray = (bets: ClientBetsType) =>
  // @ts-ignore
  Object.keys(bets).reduce((acc: IClientBet[], type: Bets) => {
    const betSelectors: string[] = Object.keys(bets[type]);

    return [
      ...acc,
      ...betSelectors.map((selector: string) => ({
        ...bets[type][selector],
        type,
        selector: selector.split(','),
      })),
    ];
  }, []);

export const arrayToBets = (betsArray: IClientBet[]) => {
  // @ts-ignore
  return betsArray.reduce((acc: IBets, next: IClientBet) => {
    const { type, currentKey } = getBetPath(next);
    return {
      ...acc,
      [type]: {
        // @ts-ignore
        ...acc[type],
        [currentKey]: { ...next },
      },
    };
  }, {});
};

export const getCurrentDozenNum = (ballPosition: string) =>
  Math.floor(Math.abs((Number(ballPosition) - 1) / 12));

export const getComplexWinners = (ballPosition: string) => {
  const currentDozenNum = getCurrentDozenNum(ballPosition);
  const currentStraight = ballPosition.toString();
  const [rowIndex] = calculateIndexes(ballPosition.toString());
  const [low, even, red, black, odd, high] = chancesBets.map(({ selector }: IClientBet) =>
    selector.toString(),
  );

  return [
    isOdd(currentStraight) ? odd : even,
    isFirst(currentStraight) ? low : high,
    isRed(currentStraight) ? red : black,
    columnBets[rowIndex].selector.toString(),
    dozenBets[currentDozenNum].selector.toString(),
  ];
};

export const getBetArrWithNeighbours = (currentItem: string, neighboursCount: number) => {
  // @ts-ignore
  const currentIndex = numberOnTheRacetrackValueIndex[currentItem];
  const leftDelta = currentIndex - neighboursCount;
  const rightDelta = currentIndex + neighboursCount + 1;
  const { length } = numberOnTheRacetrack;
  const defaultArr = numberOnTheRacetrack.slice(leftDelta < 0 ? 0 : leftDelta, rightDelta);

  if (leftDelta < 0) {
    const leftPart = numberOnTheRacetrack.slice(leftDelta);
    return [...leftPart, ...defaultArr];
  }

  if (rightDelta > length) {
    const rightPart = numberOnTheRacetrack.slice(0, rightDelta - length);
    return [...defaultArr, ...rightPart];
  }

  return defaultArr;
};

export const getStraightSelectorsWithNeighbours = (
  currentStraight: IClientBet,
  neighboursCount: number,
  selectionArr: string[] = [],
): ISelection[] => {
  const { label } = currentStraight;
  const { length } = selectionArr;
  // @ts-ignore
  const straights = length ? selectionArr : getBetArrWithNeighbours(label, neighboursCount);
  return straights.map((straight: string) => ({ selector: [straight], key: straight }));
};

export const getStraightWithNeighbours = (
  currentStraight: IClientBet,
  neighboursCount: number,
  betTypes = BetsTypes.raceTrackStraight,
) => {
  const { label } = currentStraight;
  // @ts-ignore
  const betArr = getBetArrWithNeighbours(label, neighboursCount);
  return betArr.map((item: string) => ({ label: item, selector: [item], type: betTypes }));
};

export const mergeWithPresent = (betArr: IClientBet[], presentData: IBets) =>
  // @ts-ignore
  betArr.map((item: IMakeBet) => {
    const { type, selector } = item;
    const betFromPresent = pathOr({}, [type, selector.toString()], presentData);
    const currentChips = pathOr(1, ['chips'], item);
    return { ...item, ...betFromPresent, chips: currentChips };
  });

export const getSelectionStraights = (selections: ISelection[]) =>
  selections.reduce(
    (acc: ModifiedBets, { selector, key }: ISelection) => [...acc, ...selector, key],
    [],
  );

export const calculateNeighboursBet = (bet: IMakeBet, present: ClientBetsType): IMakeBet[] => {
  const { type } = bet;

  return [{ ...bet, chips: neighboursChipsCount[type] }];
};

export const caclulateVoisinsSum = (
  currentPlacedBets: IBets,
  newBetsWithLimits: IClientBet[],
): IClientBet[] => {
  const { present } = currentPlacedBets;
  // @ts-ignore
  const placedBetsArray: IMakeBet[] = betsToBetsArray(present);
  const placedBetSumBeforeNewBet = sumBets(placedBetsArray);

  const sumBetsWithLimits = sumBets(newBetsWithLimits);

  return newBetsWithLimits.map((bet) => {
    if (bet.type === BetsTypes.voisins) {
      const newBetsSumDiff = sumBetsWithLimits - placedBetSumBeforeNewBet;
      // @ts-ignore
      const newVoisinsWithLimits = newBetsWithLimits.find(({ type }) => type === BetsTypes.voisins);
      // @ts-ignore
      const currentVoisins = present[BetsTypes.voisins];

      const newVoisinsBetValue = newVoisinsWithLimits ? newVoisinsWithLimits?.bet || 0 : 0;
      const oldVoisinsBetValue = currentVoisins ? currentVoisins[BetsTypes.voisins]?.bet || 0 : 0;

      const betsSumDiffInVoisins = newVoisinsBetValue - oldVoisinsBetValue;

      if (betsSumDiffInVoisins > newBetsSumDiff) {
        const diff = betsSumDiffInVoisins - newBetsSumDiff;
        return {
          ...bet,
          bet: bet.bet ? bet.bet - diff : 0,
        };
      }
      return bet;
    }
    return bet;
  });
};
